import React, { useState, useEffect } from "react";

import {
  Button,
  Container,
  Form,
  Modal,
  Pagination,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useWeb3ModalProvider } from "@web3modal/ethers/react";
import { Contract, ethers } from "ethers";
import { useMediaQuery } from "react-responsive";
import {
  adminKey,
  network,
  presaleAbi,
  presaleAddress,
  token,
} from "../utils/contants";
import { toast } from "react-toastify";

import { getAffiliateMap, setAffiliate } from "../store/firestore.ts";
import {
  copyToClipboard,
  exportAffiliatesToExcel,
  formatBalance,
  shortenAddress,
} from "../utils/utils.ts";
import { AffiliateData } from "../types/AffiliateData";
import { Affiliate } from "../types/Affiliate.ts";

interface Props {
  revokedOnly: boolean;
}
const AffilaiteTable: React.FC<Props> = ({ revokedOnly }) => {
  const { t } = useTranslation();
  const { walletProvider } = useWeb3ModalProvider();

  const [presaleContract, setPresaleContract] =
    useState<ethers.Contract | null>(null);
  const [affilaiteDataMap, setAffiliateMap] = useState<
    Map<string, AffiliateData>
  >(new Map());
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [totalAffiliates, setTotalAffiliates] = useState(0);

  // Pagination & search
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Edit modal state
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(
    null
  );
  const [editNote, setEditNote] = useState("");
  const [editProfit, setEditProfit] = useState(0);
  const [editRevoked, setEditRevoked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const init = async () => {
    try {
      console.log("init called");
      if (!walletProvider) return;
      console.log("init worked");
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new Contract(presaleAddress, presaleAbi, signer);
      setPresaleContract(contract);
    } catch (error) {
      console.error(error);
      toast.info(t("info.error"));
    }
  };

  const showTruncatedError = (error: unknown) => {
    const errorMessage = error?.toString() || "Unknown error";
    const truncatedMessage =
      errorMessage.length > 100
        ? `${errorMessage.slice(0, 50)}...${errorMessage.slice(-50)}`
        : errorMessage;
    toast.error(truncatedMessage);
  };

  const parseAffiliates = (data: any[]) => {
    let affs: Affiliate[] = [];
    for (let i = 0; i < data.length; i++) {
      let affiliate = Affiliate.fromEthersResponse(data[i]);
      // Stop if we hit a zero address
      if (affiliate.user === ethers.ZeroAddress) break;
      affs.push(affiliate);
    }
    return affs;
  };

  const fetchAffiliates = async () => {
    if (!presaleContract) return;
    try {
      // Call getAffiliates with pageSize and offset
      const result = await presaleContract.getAffiliates(pageSize, offset);
      const affs = parseAffiliates(result);
      if (revokedOnly) {
        let revokedAffiliates = affs.filter((item) => item.revoked);
        setAffiliates(revokedAffiliates);
      } else {
        setAffiliates(affs);
      }
      // Fetch total affiliates from contract (if available)
      if (presaleContract.getTotalAffiliates) {
        const total = await presaleContract.getTotalAffiliates();
        setTotalAffiliates(parseInt(total));
      }
    } catch (error) {
      console.error("Error fetching affiliates:", error);
    }
  };

  // Update only profit function for the modal
  const updateAffiliateProfitOnly = async () => {
    try {
      if (!presaleContract || !selectedAffiliate) {
        throw new Error("Please connect your wallet");
      }
      const tx = await presaleContract.updateAffiliateProfit(
        selectedAffiliate.code,
        editProfit
      );
      await tx.wait();
      await fetchAffiliates();
      toast.success("Profit Updated");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating profit:", error);
      showTruncatedError(error);
    }
  };

  const updateNote = async () => {
    try {
      if (!selectedAffiliate) {
        throw new Error("Affiliate not selected");
      }
      await setAffiliate(selectedAffiliate?.user, editNote);
      toast.success("Note Updated");
      setShowEditModal(false);
      initDB();
    } catch (error) {
      console.error("Error updating note", error);
      showTruncatedError(error);
    }
  };

  // Update only revoke status function for the modal
  const updateAffiliateRevokeOnly = async () => {
    try {
      if (!presaleContract || !selectedAffiliate) {
        throw new Error("Please connect your wallet");
      }
      const tx = await presaleContract.updateRevokeStatus(
        selectedAffiliate.code,
        editRevoked
      );
      await tx.wait();
      await fetchAffiliates();
      toast.success("Revoked Status Updated");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating revoke status:", error);
      showTruncatedError(error);
    }
  };

  const performCopy = async (text: string) => {
    const res = await copyToClipboard(text);
    if (res) {
      toast.success("Copied to clipboard");
    } else {
      toast.error("Failed to copy");
    }
  };

  // New search function: Query contract mappings directly.
  const searchUser = async () => {
    if (!presaleContract) return;
    if (!searchQuery.trim()) {
      // Reset list if search query is empty
      await fetchAffiliates();
      return;
    }
    try {
      let affiliate;
      // If query is a valid address, search by codeMap first.
      if (ethers.isAddress(searchQuery)) {
        const code = await presaleContract.codeMap(searchQuery);
        if (!code || code === "") {
          toast.info("Affiliate not present");
          setAffiliates([]);
          return;
        }
        affiliate = await presaleContract.affiliateMap(code);
      } else {
        // Otherwise, assume query is a referral code.
        affiliate = await presaleContract.affiliateMap(searchQuery);
      }
      if (affiliate.user === ethers.ZeroAddress) {
        toast.info("Affiliate not present");
        setAffiliates([]);
      } else {
        setAffiliates([Affiliate.fromEthersResponse(affiliate)]);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Error during search");
    }
  };

  // Pagination handlers
  const handlePrevPage = async () => {
    if (offset > 0) {
      setOffset(offset - 1);
    }
  };

  const handleNextPage = async () => {
    // Ensure that there is another page (using totalAffiliates if available)
    if (totalAffiliates > (offset + 1) * pageSize) {
      setOffset(offset + 1);
    }
  };

  // Whenever offset changes, re-fetch affiliates
  useEffect(() => {
    if (presaleContract) {
      fetchAffiliates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  useEffect(() => {
    init();
  }, [walletProvider]);

  useEffect(() => {
    fetchAffiliates();
  }, [presaleContract]);

  const initDB = async () => {
    let affiliates = await getAffiliateMap();
    setAffiliateMap(affiliates);
  };

  useEffect(() => {
    initDB();
  }, []);
  return (
    <Container>
      {/* Search */}
      <div className="d-flex justify-content-center align-items-center mb-3">
        <div className="flex-grow-1">
          <h5>{t("affiliates.affiliates_list")}</h5>
        </div>
        <div className="me-3">
          <Button
            onClick={() => {
              exportAffiliatesToExcel(affiliates, affilaiteDataMap);
            }}
            variant="primary"
          >
            {t("affiliates.export")}
          </Button>
        </div>
        <div>
          <Form className="me-3">
            <Form.Control
              type="text"
              placeholder={t("affiliates.search_by_code_or_user")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
          </Form>
        </div>
        <div>
          <Button onClick={searchUser} variant="primary">
            {t("affiliates.search")}
          </Button>
        </div>
      </div>
      {/* Affiliates Table */}
      <div
        style={{
          maxWidth: isMobile ? "100%" : "calc(100vw - 358px)",
        }}
      >
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>SNO</th>
              <th>{t("affiliates.referal_code")}</th>
              <th>{t("affiliates.note")} </th>
              <th>{t("affiliates.panel")}</th>
              <th>{t("affiliates.user_address")}</th>
              <th>{t("affiliates.sale")}</th>
              <th>{t("affiliates.profit")} USDT</th>
              <th>
                {t("affiliates.profit")} {network.currency}
              </th>
              <th>{t("affiliates.available")} USDT</th>
              <th>
                {t("affiliates.available")} {network.currency}
              </th>
              <th>{t("affiliates.profit")} %</th>
              <th>{t("affiliates.revoked")} </th>
              <th>{t("affiliates.actions")} </th>
            </tr>
          </thead>
          <tbody>
            {affiliates.map((aff, index) => (
              <tr key={index} style={{'whiteSpace' : 'nowrap'}}>
                <td>{offset * pageSize + index + 1}</td>
                <td>
                  <div className="d-flex justify-content-between align-items-center">
                    {aff.code}
                  </div>
                </td>
                <td>{affilaiteDataMap.get(aff.user)?.note ?? "--"}</td>
                <td>
                  <a
                    style={{ textDecoration: "none", marginLeft: "4px" }}
                    target="_blank"
                    href={`https://ksc-distributor.netlify.app?address=${aff.user}&key=${adminKey}`}
                    rel="noreferrer"
                  >
                    View
                  </a>
                </td>
                <td>
                  <div className="d-flex justify-content-between align-items-center">
                    {/* {shortenAddress(aff.user, 5)} */}
                    {aff.user}
                    <img
                      src="/copy-file-icon.svg"
                      height={24}
                      width={24}
                      className="ms-3"
                      style={{ cursor: "pointer" }}
                      alt="copy"
                      onClick={() => performCopy(aff.user)}
                    />
                  </div>
                </td>
                <td>
                  {formatBalance(aff.tokensSold)} {token.symbol}
                </td>
                <td>{formatBalance(aff.usdtProfit, 6)} USDT</td>
                {/* <td>{(aff.nativeProfit.toString())} ETH</td> */}
                <td>{formatBalance(aff.nativeProfit, 18)} ETH</td>
                <td>
                  {formatBalance(
                    aff.usdtProfit.sub(aff.usdtWithdrawn).toString(),
                    6
                  )}{" "}
                  USDT
                </td>
                <td>
                  {formatBalance(
                    aff.nativeProfit.sub(aff.nativeWithdrawn).toString(),
                    18
                  )}{" "}
                  {network.currency}
                </td>
                <td>{aff.profitPercent}%</td>
                <td>{aff.revoked ? "Yes" : "No"}</td>
                <td>
                  <Button
                    variant="warning"
                    onClick={() => {
                      setSelectedAffiliate(Affiliate.fromJSON(aff.toJSON()));
                      setEditNote(affilaiteDataMap.get(aff.user)?.note ?? "");
                      setEditProfit(aff.profitPercent);
                      setEditRevoked(aff.revoked);
                      setShowEditModal(true);
                    }}
                  >
                    {t("affiliates.edit")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Pagination className="justify-content-center">
        <Pagination.Prev disabled={offset === 0} onClick={handlePrevPage} />
        <Pagination.Item active>{offset + 1}</Pagination.Item>
        <Pagination.Next
          disabled={totalAffiliates <= (offset + 1) * pageSize}
          onClick={handleNextPage}
        />
      </Pagination>

      {/* Edit Affiliate Modal with three buttons */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("affiliates.edit_affiliate")} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAffiliate && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t("affiliates.profit_percentage")} </Form.Label>
                <Form.Control
                  type="number"
                  value={editProfit}
                  onChange={(e) => setEditProfit(Number(e.target.value))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("affiliates.note")} </Form.Label>
                <Form.Control
                  type="text"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={t("affiliates.revoked")}
                  checked={editRevoked}
                  onChange={(e) => setEditRevoked(e.target.checked)}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            {t("affiliates.cancel")}
          </Button>
          <Button variant="primary" onClick={updateAffiliateProfitOnly}>
            {t("affiliates.update_profit")}
          </Button>
          <Button variant="info" onClick={updateAffiliateRevokeOnly}>
            {t("affiliates.update_revoke")}
          </Button>
          <Button variant="info" onClick={updateNote}>
            {t("affiliates.save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AffilaiteTable;
